import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Edit3,
  Archive,
  XCircle,
  Download,
  Calendar,
  DollarSign,
  Building2,
  User,
  Clock,
  FileText,
  AlertTriangle,
  ChevronLeft,
  Info,
  Target,
  CheckCircle2,
  Activity,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { processApi, documentApi, apiClient, chatApi } from "@/services/api";
import { Process } from "@/types/process";
import ProcessDocumentsCard from "@/components/processes/ProcessDetails/ProcessDocumentsCard";
import { VisualTimeline } from "@/components/documents/VisualTimeline";
import { FeatureInDevelopment } from "@/components/ui/feature-in-development";
import { useAuth } from "@/contexts/AuthContext";
import { getProcessStatusColor, getProcessStatusLabel, PROCESS_STATUS } from "@/constants/process-status";

interface Modality {
  id: number;
  name: string;
}

export default function ProcessTimelineV3() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // Fetch process data
  // NOTE: Backend limitation - GET /processes/{id}/ does not include documents array
  // Documents must be fetched separately using GET /documents/?process={id}
  const {
    data: process,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["process", id],
    queryFn: () => processApi.get(id!),
    enabled: !!id,
  });

  // Fetch documents for this process
  const { data: documentsResponse } = useQuery({
    queryKey: ["process-documents", id],
    queryFn: async () => {
      const response = await documentApi.getByProcess(id!);
      const docs = Array.isArray(response) ? response : (response as any)?.results || [];
      return docs;
    },
    enabled: !!id,
  });


  // Fetch modalities from backend
  const { data: modalities = [] } = useQuery<Modality[]>({
    queryKey: ["modalities"],
    queryFn: async () => {
      try {
        const response = await apiClient.get("/modalities/");
        return Array.isArray(response.data)
          ? response.data
          : response.data.results || [];
      } catch (error) {
        console.error("Error fetching modalities:", error);
        return [];
      }
    },
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async () => {
      // TODO: Backend needs archive endpoint
      // await processApi.archive(id!);
      return processApi.update(id!, { status: PROCESS_STATUS.ARQUIVADO });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["process", id] });
      queryClient.invalidateQueries({ queryKey: ["processes"] });
      setShowArchiveModal(false);
      navigate("/processes");
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      // TODO: Backend needs cancel endpoint with reason field
      // await processApi.cancel(id!, cancelReason);
      return processApi.update(id!, { status: PROCESS_STATUS.ARQUIVADO });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["process", id] });
      queryClient.invalidateQueries({ queryKey: ["processes"] });
      setShowCancelModal(false);
      setCancelReason("");
      navigate("/processes");
    },
  });


  const getModalityLabel = (modalityId: number | undefined) => {
    if (!modalityId) return "Não definida";
    const modality = modalities.find((m) => m.id === modalityId);
    return modality?.name || "Não definida";
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (!value) return "R$ 0,00";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numValue);
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return "Não definida";
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (date: string | null | undefined, time: string | null | undefined) => {
    if (!date) return "Não definida";
    const dateStr = formatDate(date);
    if (!time) return dateStr;
    return `${dateStr} às ${time}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
      </div>
    );
  }

  if (error || !process) {
    return (
      <div className="container mx-auto px-6 py-8">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Erro ao carregar o processo. Por favor, tente novamente.
          </AlertDescription>
        </Alert>
        <Button
          variant="ghost"
          onClick={() => navigate("/processes")}
          className="mt-4"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Voltar para processos
        </Button>
      </div>
    );
  }

  // TODO: Calculate deadline and progress from backend data when ProcessStage is available
  // const daysUntilDeadline = process.deadline_date ? differenceInDays(new Date(process.deadline_date), new Date()) : null;
  // const completedStages = stages.filter(s => s.status === 'approved').length;
  // const totalStages = stages.length;
  // const progress = (completedStages / totalStages) * 100;

  return (
    <div className="p-6 lg:p-8 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/processes")}
            className="border-navy-500 hover:bg-navy-700 flex-shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <h1 className="text-3xl font-bold text-foreground">
                {process.object || "Processo sem título"}
              </h1>
              <Badge className={`border ${getProcessStatusColor(process.status)}`}>
                {getProcessStatusLabel(process.status)}
              </Badge>
            </div>

            {/* Quick Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              {process.code && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <FileText className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Processo:</strong> {process.code}
                  </span>
                </div>
              )}

              {process.modality?.id && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Modalidade:</strong> {getModalityLabel(Number(process.modality.id))}
                  </span>
                </div>
              )}

              {process.estimated_value && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <DollarSign className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Valor:</strong> {formatCurrency(process.estimated_value)}
                  </span>
                </div>
              )}

              {process.responsible && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Responsável:</strong> {process.responsible}
                  </span>
                </div>
              )}

              {process.publication_date && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Publicação:</strong> {formatDate(process.publication_date)}
                  </span>
                </div>
              )}

              {process.opening_date && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Abertura:</strong> {formatDateTime(process.opening_date, process.opening_time)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate(`/processes/${id}/edit`)}
            className="bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-500/90 text-white shadow-lg shadow-blue-900/30"
          >
            <Edit3 className="mr-2 h-4 w-4" />
            Editar Processo
          </Button>
          {/* TODO: Implementar lógica de arquivar e cancelar no backend antes de habilitar */}
          {/* <Button
            variant="outline"
            onClick={() => setShowArchiveModal(true)}
            className="border-navy-500 hover:bg-navy-700"
            disabled={process.status === "archived"}
          >
            <Archive className="mr-2 h-4 w-4" />
            Arquivar
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCancelModal(true)}
            className="border-red-500 text-red-400 hover:bg-red-900/20"
            disabled={process.status === "cancelled"}
          >
            <XCircle className="mr-2 h-4 w-4" />
            Cancelar
          </Button> */}

          {/* TODO: Implementar download de documentos quando disponível */}
          <Button
            variant="outline"
            className="border-navy-500 hover:bg-navy-700"
            disabled
          >
            <Download className="mr-2 h-4 w-4" />
            Baixar Documentos
          </Button>
        </div>

        {/* TODO: Credit and Legal Alerts - Backend needs CreditPricing and LegalParameters entities */}
        {/* Implement alert cards here when backend is ready */}

        {/* Progress Section - Integration with VisualTimeline */}
        <VisualTimeline
          process={process}
          documents={documentsResponse || []}
          onStageClick={(stage) => {
            const docIdOrObj = stage.id;
            const generatedDoc = (documentsResponse || []).find(d => {
              const tId = typeof d.document_type === 'object' ? d.document_type.id : d.document_type;
              return String(tId) === String(docIdOrObj);
            });

            if (generatedDoc) {
              navigate(`/documents/${generatedDoc.id}`);
            }
          }}
          onGenerateAI={(stage) => {
            navigate(`/documents/new?process=${process.id}&type=${stage.id}`);
          }}
          onCreateEdit={async (stage, existingDoc) => {
            if (existingDoc) {
              try {
                // Verify if there is an active chat session for this document
                const activeSessions = await chatApi.getActiveSessions(existingDoc.id);
                if (activeSessions && activeSessions.length > 0) {
                  // Reopen chat memory
                  navigate(`/chat8/${existingDoc.id}`);
                  return;
                }
              } catch (error) {
                console.error("Error checking active chat sessions:", error);
              }
              // Normal advanced editor fallback
              navigate(`/documents/${existingDoc.id}`);
            } else {
              navigate(`/documents/new?process=${process.id}&type=${stage.id}`);
            }
          }}
          onDismiss={(stage) => {
            // TODO: Implementar lógica de dispensar estágio no backend
            console.log('Dispensar estágio:', stage.name);
          }}
        />

        {/* Process Details */}
        <Card className="card-navy-glass border-navy-500">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Info className="w-6 h-6 text-primary" />
              Detalhes Completos do Processo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Informações Básicas
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {process.code && (
                  <div>
                    <p className="text-muted-foreground">Número do Processo</p>
                    <p className="text-foreground font-semibold">{process.code}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge className={`border ${getProcessStatusColor(process.status)}`}>
                    {getProcessStatusLabel(process.status)}
                  </Badge>
                </div>
                {process.modality?.id && (
                  <div>
                    <p className="text-muted-foreground">Modalidade</p>
                    <p className="text-foreground font-semibold">{getModalityLabel(Number(process.modality.id))}</p>
                  </div>
                )}
                {process.estimated_value && (
                  <div>
                    <p className="text-muted-foreground">Valor Estimado</p>
                    <p className="text-foreground font-semibold">{formatCurrency(process.estimated_value)}</p>
                  </div>
                )}
                {process.publication_date && (
                  <div>
                    <p className="text-muted-foreground">Data de Publicação</p>
                    <p className="text-foreground font-semibold">{formatDate(process.publication_date)}</p>
                  </div>
                )}
                {process.opening_date && (
                  <div>
                    <p className="text-muted-foreground">Data de Abertura</p>
                    <p className="text-foreground font-semibold">{formatDateTime(process.opening_date, process.opening_time)}</p>
                  </div>
                )}
                {process.responsible && (
                  <div>
                    <p className="text-muted-foreground">Responsável</p>
                    <p className="text-foreground font-semibold">{process.responsible}</p>
                  </div>
                )}

              </div>
            </div>

            {/* Object Summary */}
            {process.object && (
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Objeto do Processo
                </h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-navy-900 p-4 rounded-lg border border-navy-500">
                  {process.object}
                </p>
              </div>
            )}

            {/* Description/Notes */}
            {process.description && (
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Descrição Adicional
                </h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-navy-900 p-4 rounded-lg border border-navy-500">
                  {process.description}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents Section */}
        {/* Usando o Componente Original ProcessDocumentsCard para lidar com os PDFs */}
        <ProcessDocumentsCard process={process} documents={documentsResponse || []} />

        {/* AI Diagnostic System - Feature in Development */}
        <FeatureInDevelopment
          title="Diagnóstico do Sistema IA"
          description="Sistema de verificação automática de pré-requisitos para geração de documentos via IA."
          features={[
            "Validação de agente PENSADOR, tabela de preços e saldo de créditos",
            "Verificação de mapeamentos de herança e dados do processo/etapa",
            "Sugestões automáticas de correção para problemas detectados",
            "Indicadores visuais (verde/amarelo/vermelho) com detalhes técnicos expandíveis",
          ]}
        />


        {/* Archive Modal */}
        <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
          <DialogContent className="bg-navy-800 border-navy-500">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                Arquivar Processo
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Tem certeza que deseja arquivar este processo? Ele não será
                excluído, apenas movido para o arquivo.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowArchiveModal(false)}
                className="border-navy-500 hover:bg-navy-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => archiveMutation.mutate()}
                disabled={archiveMutation.isPending}
                className="gradient-primary"
              >
                {archiveMutation.isPending ? "Arquivando..." : "Arquivar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Modal */}
        <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
          <DialogContent className="bg-navy-800 border-navy-500">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                Cancelar Processo
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Tem certeza que deseja cancelar este processo? Esta ação não pode
                ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium text-foreground mb-2 block">
                Motivo do cancelamento (opcional)
              </label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Descreva o motivo do cancelamento..."
                className="bg-navy-900 border-navy-500 text-foreground"
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason("");
                }}
                className="border-navy-500 hover:bg-navy-700"
              >
                Voltar
              </Button>
              <Button
                variant="destructive"
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? "Cancelando..." : "Cancelar Processo"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


      </div>
    </div>
  );
}
